// Sprint G4 · Interfaz oficial de Roles y Permisos.
// Aislada: vive bajo /rbac_official/ y no toca los flujos legacy.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as rbac from "./api";

// ─── Utilidades UI ─────────────────────────────────────────────────────────

function extractError(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || "Error desconocido";
  if (typeof data.detail === "object" && data.detail !== null) {
    const code = data.detail.code;
    const msg = data.detail.message || code;
    if (code === "MISSING_REASON") return "El motivo es obligatorio.";
    if (code === "SELF_ADMIN") return "No puedes modificar tus propios privilegios.";
    if (code === "LAST_ADMIN") return "No puedes dejar al sistema sin Propietario/Admin.";
    if (code === "UNKNOWN_PERMISSION") return "Permiso no oficial.";
    if (code === "UNKNOWN_PERMISSIONS") return "Uno o más IDs no pertenecen al catálogo.";
    if (code === "ROLE_EXISTS") return msg;
    return msg;
  }
  if (typeof data === "string") return data;
  return "Error";
}

const ORIGIN_LABELS = {
  "admin-role": "Admin",
  "role+grant": "Rol + Grant",
  role: "Rol",
  grant: "Grant",
  "revoked-role": "Rol (revocado)",
  "revoked-grant": "Grant (revocado)",
  none: "—",
};

const ORIGIN_STYLE = {
  "admin-role": "bg-purple-100 text-purple-800",
  "role+grant": "bg-blue-100 text-blue-800",
  role: "bg-emerald-100 text-emerald-800",
  grant: "bg-amber-100 text-amber-900",
  "revoked-role": "bg-red-100 text-red-700 line-through",
  "revoked-grant": "bg-red-100 text-red-700 line-through",
  none: "bg-neutral-100 text-neutral-500",
};

// ─── ReasonDialog ──────────────────────────────────────────────────────────

function ReasonDialog({ open, title, description, initial, onCancel, onConfirm }) {
  const [reason, setReason] = useState(initial || "");
  useEffect(() => { setReason(initial || ""); }, [initial, open]);
  if (!open) return null;
  const trimmed = reason.trim();
  return (
    <div
      data-testid="rbac-reason-dialog"
      className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
    >
      <div className="bg-white shadow-2xl max-w-md w-full p-6 border-l-4 border-l-[#7C3AED]">
        <h3 className="font-display font-bold text-xl mb-1">{title}</h3>
        {description && <p className="text-sm text-neutral-600 mb-4">{description}</p>}
        <label className="block text-xs font-mono-tech tracking-[0.15em] font-bold text-neutral-600 mb-1">
          MOTIVO (OBLIGATORIO)
        </label>
        <textarea
          data-testid="rbac-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          className="w-full border-2 border-neutral-300 p-2 text-sm focus:border-[#7C3AED] outline-none"
          placeholder="Explica el motivo del cambio…"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="rbac-reason-cancel"
            onClick={onCancel}
            className="px-4 py-2 border-2 border-neutral-300 text-sm hover:bg-neutral-50"
          >
            Cancelar
          </button>
          <button
            data-testid="rbac-reason-confirm"
            disabled={!trimmed}
            onClick={() => onConfirm(trimmed)}
            className="px-4 py-2 bg-[#7C3AED] text-white text-sm font-bold hover:bg-[#5B21B6] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PreviewDialog ─────────────────────────────────────────────────────────

function PreviewDialog({ open, before, after, onCancel, onConfirm }) {
  if (!open) return null;
  const diff = (label, a, b) => {
    const va = JSON.stringify(a);
    const vb = JSON.stringify(b);
    if (va === vb) return null;
    return (
      <div key={label} className="mb-2">
        <div className="text-[0.7rem] font-mono-tech tracking-[0.15em] font-bold text-neutral-600">{label}</div>
        <div className="text-xs text-red-700">antes: {va}</div>
        <div className="text-xs text-emerald-700">después: {vb}</div>
      </div>
    );
  };
  const rows = [
    diff("Roles", before.rbac_role_names, after.rbac_role_names),
    diff("Grants", before.rbac_grants, after.rbac_grants),
    diff("Revokes", before.rbac_revokes, after.rbac_revokes),
    diff("branch_scope", before.branch_scope, after.branch_scope),
    diff("Activo", before.is_active, after.is_active),
  ].filter(Boolean);
  return (
    <div
      data-testid="rbac-preview-dialog"
      className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
    >
      <div className="bg-white shadow-2xl max-w-lg w-full p-6 border-l-4 border-l-emerald-500">
        <h3 className="font-display font-bold text-xl mb-4">Vista previa</h3>
        {rows.length === 0 ? (
          <div className="text-sm text-neutral-500">Sin cambios.</div>
        ) : (
          rows
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="rbac-preview-cancel"
            onClick={onCancel}
            className="px-4 py-2 border-2 border-neutral-300 text-sm hover:bg-neutral-50"
          >
            Cerrar
          </button>
          {rows.length > 0 && (
            <button
              data-testid="rbac-preview-confirm"
              onClick={onConfirm}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700"
            >
              Guardar cambios
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PermissionRow ─────────────────────────────────────────────────────────

function PermissionRow({ perm, disabled, onToggle }) {
  const label = ORIGIN_LABELS[perm.origin] || perm.origin;
  const badgeCls = ORIGIN_STYLE[perm.origin] || "bg-neutral-100 text-neutral-500";
  return (
    <label
      data-testid={`rbac-perm-row-${perm.id}`}
      className="flex items-start gap-3 py-2 px-3 hover:bg-neutral-50 border-b border-neutral-100"
    >
      <input
        type="checkbox"
        data-testid={`rbac-perm-check-${perm.id}`}
        checked={!!perm.effective}
        disabled={disabled}
        onChange={() => onToggle(perm)}
        className="mt-1 h-4 w-4 accent-[#7C3AED]"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <code className="font-mono-tech text-xs font-bold">{perm.id}</code>
          <span className={`text-[0.65rem] px-2 py-0.5 font-bold tracking-wide ${badgeCls}`}>
            {label}
          </span>
          {perm.inherited_from && perm.inherited_from.length > 0 && (
            <span className="text-[0.65rem] text-neutral-500">
              origen: {perm.inherited_from.join(", ")}
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-600 mt-0.5">{perm.description}</div>
      </div>
    </label>
  );
}

// ─── RbacAdminPage ─────────────────────────────────────────────────────────

export default function RbacAdminPage({ currentUserId, onBack }) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [reasonDialog, setReasonDialog] = useState(null); // { title, description, onConfirm }
  const [preview, setPreview] = useState(null); // { before, after }

  // Local pending edits (grants/revokes/roles/scope) — only committed on save.
  const [pending, setPending] = useState(null);

  const isSelf = selectedId && currentUserId && selectedId === currentUserId;

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, b, e, a] = await Promise.all([
        rbac.listRoles(),
        rbac.listPermissions(),
        rbac.listBranches(),
        rbac.listEmployees(),
        rbac.listAudit(30),
      ]);
      setRoles(r);
      setPermissions(p);
      setBranches(b);
      setEmployees(e);
      setAuditEvents(a);
      if (!selectedId && e.length > 0) {
        setSelectedId(e[0].subject_id);
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const data = await rbac.getEmployee(id);
      setDetail(data);
      setPending({
        rbac_role_names: [...data.profile.rbac_role_names],
        rbac_grants: [...data.profile.rbac_grants],
        rbac_revokes: [...data.profile.rbac_revokes],
        branch_scope: data.profile.branch_scope
          ? { mode: data.profile.branch_scope.mode, branch_ids: [...data.profile.branch_scope.branch_ids] }
          : { mode: "all", branch_ids: [] },
        is_active: data.profile.is_active,
      });
    } catch (err) {
      toast.error(extractError(err));
    }
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  // Compute a "preview profile" from local pending edits.
  const previewMatrix = useMemo(() => {
    if (!detail || !pending) return null;
    const rolesMap = Object.fromEntries(roles.map((r) => [r.name, r]));
    const inherited = new Map(); // pid -> [role names]
    const isAdmin = pending.rbac_role_names.some((n) => rolesMap[n]?.is_admin);
    pending.rbac_role_names.forEach((n) => {
      const r = rolesMap[n];
      if (!r) return;
      if (r.is_admin) {
        permissions.forEach((p) => {
          const arr = inherited.get(p.id) || [];
          arr.push(n);
          inherited.set(p.id, arr);
        });
      } else {
        (r.permissions || []).forEach((pid) => {
          const arr = inherited.get(pid) || [];
          arr.push(n);
          inherited.set(pid, arr);
        });
      }
    });
    return permissions.map((p) => {
      const src = inherited.get(p.id) || [];
      const granted = pending.rbac_grants.includes(p.id);
      const revoked = pending.rbac_revokes.includes(p.id);
      const effective = isAdmin ? true : ((src.length > 0 && !revoked) || (granted && !revoked));
      let origin = "none";
      if (isAdmin) origin = "admin-role";
      else if (src.length > 0 && granted && !revoked) origin = "role+grant";
      else if (src.length > 0 && !revoked) origin = "role";
      else if (granted && !revoked) origin = "grant";
      else if (revoked && src.length > 0) origin = "revoked-role";
      else if (revoked && granted) origin = "revoked-grant";
      return {
        ...p,
        inherited_from: src,
        granted,
        revoked,
        effective,
        origin,
      };
    });
  }, [detail, pending, roles, permissions]);

  const toggleRole = (roleName) => {
    if (!pending) return;
    setPending((prev) => {
      const has = prev.rbac_role_names.includes(roleName);
      return {
        ...prev,
        rbac_role_names: has
          ? prev.rbac_role_names.filter((n) => n !== roleName)
          : [...prev.rbac_role_names, roleName],
      };
    });
  };

  const togglePermission = (perm) => {
    if (!pending) return;
    setPending((prev) => {
      const inherited = perm.inherited_from && perm.inherited_from.length > 0;
      const isGrant = prev.rbac_grants.includes(perm.id);
      const isRevoke = prev.rbac_revokes.includes(perm.id);
      // Current effective per pending state.
      const rolesMap = Object.fromEntries(roles.map((r) => [r.name, r]));
      const isAdmin = prev.rbac_role_names.some((n) => rolesMap[n]?.is_admin);
      const wasEffective = isAdmin || ((inherited && !isRevoke) || (isGrant && !isRevoke));
      const next = { ...prev, rbac_grants: [...prev.rbac_grants], rbac_revokes: [...prev.rbac_revokes] };
      if (wasEffective) {
        // Deshabilitar: si heredado → añadir revoke, si grant → quitar grant.
        if (inherited && !isRevoke) next.rbac_revokes = [...next.rbac_revokes, perm.id];
        if (isGrant) next.rbac_grants = next.rbac_grants.filter((x) => x !== perm.id);
      } else {
        // Habilitar: si estaba revocado → quitar revoke, si no heredado → añadir grant.
        if (isRevoke) next.rbac_revokes = next.rbac_revokes.filter((x) => x !== perm.id);
        if (!inherited && !isGrant) next.rbac_grants = [...next.rbac_grants, perm.id];
      }
      return next;
    });
  };

  const setScopeMode = (mode) => {
    setPending((prev) => ({
      ...prev,
      branch_scope: {
        mode,
        branch_ids: mode === "all" ? [] : prev.branch_scope.branch_ids,
      },
    }));
  };

  const toggleBranchId = (bid) => {
    setPending((prev) => {
      const has = prev.branch_scope.branch_ids.includes(bid);
      const next = has
        ? prev.branch_scope.branch_ids.filter((x) => x !== bid)
        : [...prev.branch_scope.branch_ids, bid];
      let mode = prev.branch_scope.mode;
      if (mode === "one" && next.length > 1) mode = "many";
      return { ...prev, branch_scope: { mode, branch_ids: next } };
    });
  };

  // ── Diff cálculo ─────────────────────────────────────────────────────────

  const diffOps = () => {
    const before = detail.profile;
    const ops = [];
    // Roles.
    const rolesA = new Set(before.rbac_role_names);
    const rolesB = new Set(pending.rbac_role_names);
    if (rolesA.size !== rolesB.size || [...rolesA].some((n) => !rolesB.has(n))) {
      ops.push({ type: "roles", role_names: [...rolesB] });
    }
    // Grants.
    const gA = new Set(before.rbac_grants);
    const gB = new Set(pending.rbac_grants);
    [...gB].filter((x) => !gA.has(x)).forEach((pid) => ops.push({ type: "add_grant", permission_id: pid }));
    [...gA].filter((x) => !gB.has(x)).forEach((pid) => ops.push({ type: "remove_grant", permission_id: pid }));
    // Revokes.
    const rA = new Set(before.rbac_revokes);
    const rB = new Set(pending.rbac_revokes);
    [...rB].filter((x) => !rA.has(x)).forEach((pid) => ops.push({ type: "add_revoke", permission_id: pid }));
    [...rA].filter((x) => !rB.has(x)).forEach((pid) => ops.push({ type: "remove_revoke", permission_id: pid }));
    // Branch scope.
    const scopeA = JSON.stringify(before.branch_scope || null);
    const scopeB = JSON.stringify(pending.branch_scope || null);
    if (scopeA !== scopeB) {
      ops.push({ type: "branch_scope", scope: pending.branch_scope });
    }
    return ops;
  };

  const applyOps = async (ops, reason) => {
    setSaving(true);
    try {
      let last = null;
      for (const op of ops) {
        if (op.type === "roles") last = await rbac.updateRoles(selectedId, op.role_names, reason);
        else if (op.type === "add_grant") last = await rbac.addGrant(selectedId, op.permission_id, reason);
        else if (op.type === "remove_grant") last = await rbac.removeGrant(selectedId, op.permission_id, reason);
        else if (op.type === "add_revoke") last = await rbac.addRevoke(selectedId, op.permission_id, reason);
        else if (op.type === "remove_revoke") last = await rbac.removeRevoke(selectedId, op.permission_id, reason);
        else if (op.type === "branch_scope") last = await rbac.updateBranchScope(selectedId, op.scope, reason);
      }
      if (last) setDetail(last);
      await loadDetail(selectedId);
      const [emps, aud] = await Promise.all([rbac.listEmployees(), rbac.listAudit(30)]);
      setEmployees(emps);
      setAuditEvents(aud);
      toast.success("Cambios aplicados");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!pending || !detail) return;
    const ops = diffOps();
    if (ops.length === 0) {
      toast.info("Sin cambios pendientes");
      return;
    }
    setPreview({ before: detail.profile, after: pending, ops });
  };

  const handlePreviewConfirm = () => {
    setPreview((prev) => {
      const p = prev;
      setReasonDialog({
        title: "Confirmar cambios",
        description: "Introduce el motivo que quedará registrado en la auditoría (§10.2).",
        onConfirm: async (reason) => {
          setReasonDialog(null);
          setPreview(null);
          await applyOps(p.ops, reason);
        },
      });
      return p;
    });
  };

  const askForReason = (title, description, action) => {
    setReasonDialog({
      title,
      description,
      onConfirm: async (reason) => {
        setReasonDialog(null);
        try {
          setSaving(true);
          await action(reason);
          await loadDetail(selectedId);
          const [emps, aud] = await Promise.all([rbac.listEmployees(), rbac.listAudit(30)]);
          setEmployees(emps);
          setAuditEvents(aud);
          toast.success("Aplicado");
        } catch (err) {
          toast.error(extractError(err));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleRestore = () => {
    askForReason(
      "Restaurar permisos recomendados",
      "Eliminará todos los grants y revokes del empleado.",
      async (reason) => rbac.restoreRecommended(selectedId, reason),
    );
  };

  const handleForcePassword = () => {
    askForReason(
      "Forzar cambio de contraseña",
      "Se invalidará la sesión activa del empleado (§4.3).",
      async (reason) => rbac.forcePasswordChange(selectedId, reason),
    );
  };

  const handleActiveToggle = () => {
    const willBe = !pending.is_active;
    askForReason(
      willBe ? "Reactivar empleado" : "Desactivar empleado",
      willBe ? "Se restablece el acceso." : "Se invalidará su sesión inmediatamente (§4.3).",
      async (reason) => rbac.updateActive(selectedId, willBe, reason),
    );
  };

  const handleCreateRole = () => {
    const name = window.prompt("Nombre del nuevo rol:");
    if (!name) return;
    const picks = window.prompt(
      "IDs de permisos (separados por coma) — sólo del catálogo oficial:",
      "",
    );
    if (picks === null) return;
    const ids = picks.split(",").map((x) => x.trim()).filter(Boolean);
    askForReason(
      "Crear rol adicional",
      `Se creará el rol "${name}" con ${ids.length} permiso(s) del catálogo.`,
      async (reason) => {
        await rbac.createRole({ name, permissions: ids, reason });
        setRoles(await rbac.listRoles());
      },
    );
  };

  const selectedEmployee = employees.find((e) => e.subject_id === selectedId);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[var(--bg,#F5F5F0)]" data-testid="rbac-admin-page">
      <div className="max-w-[1400px] mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-mono-tech text-[0.65rem] tracking-[0.3em] font-bold text-neutral-500">
              G4 · RBAC OFICIAL
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Roles y Permisos
            </h1>
          </div>
          <button
            data-testid="rbac-close"
            onClick={onBack}
            className="px-4 py-2 border-2 border-neutral-300 text-sm hover:bg-neutral-100 font-mono-tech font-bold tracking-wide"
          >
            ← Cerrar
          </button>
        </div>

        {loading ? (
          <div className="text-neutral-500 font-mono-tech animate-pulse">Cargando…</div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            {/* Panel izquierdo: lista de empleados */}
            <aside className="col-span-12 lg:col-span-3 bg-white border-l-4 border-l-[#7C3AED]">
              <div className="p-3 border-b border-neutral-200">
                <div className="text-xs font-mono-tech tracking-widest font-bold text-neutral-600">
                  EMPLEADOS ({employees.length})
                </div>
              </div>
              <ul data-testid="rbac-employee-list" className="max-h-[70vh] overflow-y-auto">
                {employees.map((e) => (
                  <li key={e.subject_id}>
                    <button
                      data-testid={`rbac-emp-${e.subject_id}`}
                      onClick={() => setSelectedId(e.subject_id)}
                      className={`w-full text-left px-3 py-2 border-b border-neutral-100 hover:bg-neutral-50 ${
                        selectedId === e.subject_id ? "bg-purple-50 border-l-4 border-l-[#7C3AED]" : ""
                      }`}
                    >
                      <div className="text-sm font-bold">
                        {e.display_name}
                        {!e.is_active && (
                          <span className="ml-2 text-[0.65rem] px-1.5 py-0.5 bg-red-100 text-red-700">INACT.</span>
                        )}
                        {e.subject_id === currentUserId && (
                          <span className="ml-2 text-[0.65rem] px-1.5 py-0.5 bg-neutral-200 text-neutral-700">TÚ</span>
                        )}
                      </div>
                      <div className="text-[0.7rem] text-neutral-500">
                        {e.roles.length > 0 ? e.roles.join(", ") : "sin roles"}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Panel derecho: editor */}
            <section className="col-span-12 lg:col-span-9 bg-white p-4 sm:p-6">
              {!detail || !pending ? (
                <div className="text-neutral-500">Selecciona un empleado.</div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <div className="text-xs font-mono-tech tracking-wider text-neutral-500">
                        {selectedEmployee?.subject_id}
                      </div>
                      <h2 className="font-display text-2xl font-bold">
                        {selectedEmployee?.display_name}
                      </h2>
                      <div className="text-xs text-neutral-500">
                        Estado: {pending.is_active ? "activo" : "inactivo"} · Versión:{" "}
                        {detail.profile.permissions_version}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {isSelf && (
                        <span className="text-xs px-2 py-1 bg-amber-100 text-amber-900 font-bold">
                          NO-AUTOADMINISTRACIÓN — cambios desactivados
                        </span>
                      )}
                      <button
                        data-testid="rbac-toggle-active"
                        disabled={isSelf || saving}
                        onClick={handleActiveToggle}
                        className="px-3 py-1.5 border-2 border-neutral-300 text-xs hover:bg-neutral-50 disabled:opacity-40"
                      >
                        {pending.is_active ? "Desactivar" : "Reactivar"}
                      </button>
                      <button
                        data-testid="rbac-force-password"
                        disabled={isSelf || saving}
                        onClick={handleForcePassword}
                        className="px-3 py-1.5 border-2 border-neutral-300 text-xs hover:bg-neutral-50 disabled:opacity-40"
                      >
                        Forzar cambio contraseña
                      </button>
                    </div>
                  </div>

                  {/* Roles */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-mono-tech tracking-widest font-bold text-neutral-600">
                        ROLES ASIGNADOS
                      </div>
                      <button
                        data-testid="rbac-create-role"
                        onClick={handleCreateRole}
                        className="text-xs px-2 py-1 border border-neutral-300 hover:bg-neutral-50"
                      >
                        + Agregar otro rol
                      </button>
                    </div>
                    <div data-testid="rbac-roles-panel" className="flex gap-2 flex-wrap">
                      {roles.map((r) => {
                        const has = pending.rbac_role_names.includes(r.name);
                        return (
                          <button
                            key={r.name}
                            data-testid={`rbac-role-${r.name}`}
                            disabled={isSelf}
                            onClick={() => toggleRole(r.name)}
                            className={`px-3 py-1.5 text-sm border-2 disabled:opacity-40 ${
                              has
                                ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                                : "border-neutral-300 hover:bg-neutral-50"
                            }`}
                          >
                            {r.name}
                            {r.is_admin && <span className="ml-1 text-[0.65rem]">★</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Branch scope */}
                  <div className="mb-6">
                    <div className="text-xs font-mono-tech tracking-widest font-bold text-neutral-600 mb-2">
                      BRANCH_SCOPE
                    </div>
                    <div data-testid="rbac-scope-editor" className="flex gap-2 flex-wrap mb-2">
                      {[
                        { m: "one", label: "Una sucursal" },
                        { m: "many", label: "Varias sucursales" },
                        { m: "all", label: "Todas" },
                      ].map((opt) => (
                        <button
                          key={opt.m}
                          data-testid={`rbac-scope-mode-${opt.m}`}
                          disabled={isSelf}
                          onClick={() => setScopeMode(opt.m)}
                          className={`px-3 py-1.5 text-sm border-2 disabled:opacity-40 ${
                            pending.branch_scope.mode === opt.m
                              ? "bg-emerald-600 text-white border-emerald-600"
                              : "border-neutral-300"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {pending.branch_scope.mode !== "all" && (
                      <div className="flex gap-2 flex-wrap">
                        {branches.map((b) => {
                          const on = pending.branch_scope.branch_ids.includes(b.id);
                          return (
                            <button
                              key={b.id}
                              data-testid={`rbac-branch-${b.id}`}
                              disabled={isSelf}
                              onClick={() => toggleBranchId(b.id)}
                              className={`px-3 py-1 text-xs border disabled:opacity-40 ${
                                on ? "bg-emerald-100 border-emerald-500" : "border-neutral-300"
                              }`}
                            >
                              {b.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Permissions matrix */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="text-xs font-mono-tech tracking-widest font-bold text-neutral-600">
                        PERMISOS EFECTIVOS ({previewMatrix?.filter((p) => p.effective).length ?? 0} / {permissions.length})
                      </div>
                      <div className="flex gap-2">
                        <button
                          data-testid="rbac-restore"
                          disabled={isSelf || saving}
                          onClick={handleRestore}
                          className="text-xs px-3 py-1.5 border-2 border-neutral-300 hover:bg-neutral-50 disabled:opacity-40"
                        >
                          Restaurar recomendaciones
                        </button>
                      </div>
                    </div>
                    <div data-testid="rbac-perms-matrix" className="border border-neutral-200">
                      {previewMatrix?.map((p) => (
                        <PermissionRow
                          key={p.id}
                          perm={p}
                          disabled={isSelf}
                          onToggle={togglePermission}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Bottom action bar */}
                  <div className="flex gap-2 justify-end mt-6 border-t border-neutral-200 pt-4">
                    <button
                      data-testid="rbac-reset"
                      disabled={isSelf || saving}
                      onClick={() => loadDetail(selectedId)}
                      className="px-4 py-2 border-2 border-neutral-300 text-sm hover:bg-neutral-50 disabled:opacity-40"
                    >
                      Descartar
                    </button>
                    <button
                      data-testid="rbac-save"
                      disabled={isSelf || saving}
                      onClick={handleSave}
                      className="px-6 py-2 bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40"
                    >
                      Vista previa y guardar
                    </button>
                  </div>
                </>
              )}
            </section>

            {/* Audit log */}
            <section
              data-testid="rbac-audit-panel"
              className="col-span-12 bg-white p-4 border-t-2 border-t-neutral-800"
            >
              <div className="text-xs font-mono-tech tracking-widest font-bold text-neutral-600 mb-2">
                AUDITORÍA (últimos {auditEvents.length})
              </div>
              <div className="max-h-64 overflow-y-auto text-xs">
                {auditEvents.length === 0 ? (
                  <div className="text-neutral-400">Sin eventos.</div>
                ) : (
                  <table className="w-full text-left">
                    <thead className="text-[0.65rem] uppercase tracking-wider text-neutral-500">
                      <tr>
                        <th className="py-1 pr-2">Fecha</th>
                        <th className="py-1 pr-2">Acción</th>
                        <th className="py-1 pr-2">Actor</th>
                        <th className="py-1 pr-2">Target</th>
                        <th className="py-1 pr-2">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditEvents.map((e, i) => (
                        <tr key={i} className="border-t border-neutral-100">
                          <td className="py-1 pr-2 font-mono-tech text-[0.65rem]">
                            {new Date(e.timestamp).toLocaleString()}
                          </td>
                          <td className="py-1 pr-2 font-mono-tech text-[0.7rem]">{e.action}</td>
                          <td className="py-1 pr-2 font-mono-tech text-[0.65rem]">{e.actor_id?.slice(0, 8)}</td>
                          <td className="py-1 pr-2 font-mono-tech text-[0.65rem]">{e.target_id?.slice(0, 8)}</td>
                          <td className="py-1 pr-2">{e.reason || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </div>
        )}
      </div>

      <ReasonDialog
        open={!!reasonDialog}
        title={reasonDialog?.title}
        description={reasonDialog?.description}
        onCancel={() => setReasonDialog(null)}
        onConfirm={(r) => reasonDialog?.onConfirm?.(r)}
      />
      <PreviewDialog
        open={!!preview}
        before={preview?.before || {}}
        after={preview?.after || {}}
        onCancel={() => setPreview(null)}
        onConfirm={handlePreviewConfirm}
      />
    </div>
  );
}
